import React from 'react';
import { Search, X } from 'lucide-react';

import { adminContent } from 'services/api';
import type {
  AdminCatalogPage,
  QuestionLibraryDuplicateGroup,
  QuestionLibrarySearchParams,
  QuestionLibrarySearchRow,
  QuestionLibraryView,
  QuestionLibraryViewCounts,
} from 'services/api/adminContentClient';
import { CoursePicker } from 'components/admin/CoursePicker';
import { FlowPicker } from 'components/admin/FlowPicker';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { LoadingAnimation } from 'components/ui/loading-animation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { cn } from 'lib/utils';

import { CourseOption, FlowOption, QUESTION_TYPE_OPTIONS } from './types';
import { formatEditedAgo, formatQuestionTypeSlug, plainQuestionLabel } from './navigatorUtils';

const PAGE_SIZE = 25;
const GROUP_PAGE_SIZE = 100;

export const QUESTION_LIBRARY_VIEWS: Array<{ value: QuestionLibraryView; label: string }> = [
  { value: 'all', label: 'All questions' },
  { value: 'unused', label: 'Not in any module' },
  { value: 'shared', label: 'Shared by 2+ modules' },
  { value: 'empty_text', label: 'Empty text' },
  { value: 'duplicate_text', label: 'Same text as another' },
  { value: 'recent', label: 'Edited in the last 7 days' },
];

type Filters = {
  questionType: string;
  courseId: string;
  flowId: string;
  keyPrefix: string;
};

const EMPTY_FILTERS: Filters = { questionType: 'all', courseId: 'all', flowId: 'all', keyPrefix: '' };

export type QuestionSearchPanelProps = {
  selectedQuestionId: number | null;
  refreshToken: number;
  courses: CourseOption[];
  onSelectQuestion: (questionId: number) => void;
};

const useDebouncedValue = <T,>(value: T, delayMs: number) => {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [delayMs, value]);
  return debounced;
};

const SearchResultRow: React.FC<{
  row: QuestionLibrarySearchRow;
  selected: boolean;
  nested?: boolean;
  onSelect: (id: number) => void;
}> = ({ row, selected, nested, onSelect }) => {
  const label = plainQuestionLabel(row.label);
  return (
    <tr
      className={cn('cursor-pointer border-t border-black/5 hover:bg-white/50', selected && 'bg-foundation-orange-50 hover:bg-foundation-orange-50')}
      onClick={() => onSelect(row.id)}
      aria-selected={selected}
      data-testid={`ql-search-row-${row.id}`}
    >
      <td className="max-w-0 px-4 py-2">
        <button
          type="button"
          className={cn('block w-full min-w-0 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', nested && 'pl-6')}
          onClick={(event) => {
            event.stopPropagation();
            onSelect(row.id);
          }}
          aria-label={`Edit ${label || row.key}`}
        >
          <span className="block truncate font-medium text-foreground">
            {label || <span className="italic text-destructive">(empty text)</span>}
          </span>
          <span className="block truncate font-mono text-[11px] text-muted-foreground">{row.key}</span>
        </button>
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">{formatQuestionTypeSlug(row.type)}</td>
      <td className="max-w-0 px-4 py-2 text-xs">
        {row.course_count ? (
          <span className="block truncate">
            {row.courses[0]?.name}
            {row.course_count > 1 ? <span className="text-muted-foreground"> +{row.course_count - 1}</span> : null}
          </span>
        ) : row.module_count ? (
          <span className="text-muted-foreground">Module only</span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-xs">
        {row.module_count === 0 ? (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">Not in any module</span>
        ) : row.module_count > 1 ? (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-blue-700">{row.module_count} modules</span>
        ) : (
          <span className="text-muted-foreground">1 module</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">{formatEditedAgo(row.updated_at)}</td>
    </tr>
  );
};

export const QuestionSearchPanel: React.FC<QuestionSearchPanelProps> = ({
  selectedQuestionId,
  refreshToken,
  courses,
  onSelectQuestion,
}) => {
  const [view, setView] = React.useState<QuestionLibraryView>('all');
  const [searchInput, setSearchInput] = React.useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
  const keyPrefix = useDebouncedValue(filters.keyPrefix.trim(), 300);
  const [grouped, setGrouped] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [rows, setRows] = React.useState<QuestionLibrarySearchRow[]>([]);
  const [groups, setGroups] = React.useState<QuestionLibraryDuplicateGroup[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [counts, setCounts] = React.useState<QuestionLibraryViewCounts | null>(null);
  const [modules, setModules] = React.useState<FlowOption[]>([]);
  const [openGroup, setOpenGroup] = React.useState<number | null>(null);
  const [groupRows, setGroupRows] = React.useState<QuestionLibrarySearchRow[]>([]);
  const [groupRowsFor, setGroupRowsFor] = React.useState<number | null>(null);
  const [groupTotal, setGroupTotal] = React.useState(0);
  // Pages already shown, and the page currently requested (the attempt counter lets a failed page be retried).
  const [groupLoadedPage, setGroupLoadedPage] = React.useState(0);
  const [groupRequest, setGroupRequest] = React.useState({ key: '', page: 1, attempt: 0 });
  const [groupLoading, setGroupLoading] = React.useState(false);
  const [groupError, setGroupError] = React.useState(false);

  const baseParams = React.useMemo(() => {
    const params: QuestionLibrarySearchParams = {};
    if (search) params.search = search;
    if (filters.questionType !== 'all') params.question_type = filters.questionType;
    if (filters.courseId !== 'all') params.course_id = filters.courseId;
    if (filters.flowId !== 'all') params.flow_id = filters.flowId;
    if (keyPrefix) params.key_prefix = keyPrefix;
    return params;
  }, [filters.courseId, filters.flowId, filters.questionType, keyPrefix, search]);

  React.useEffect(() => {
    setPage(1);
    setOpenGroup(null);
  }, [baseParams, grouped, view]);

  React.useEffect(() => {
    let cancelled = false;
    adminContent.questions
      .viewCounts(baseParams)
      .then((response) => {
        if (!cancelled) setCounts(response.data as QuestionLibraryViewCounts);
      })
      .catch(() => {
        if (!cancelled) setCounts(null);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, refreshToken]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminContent.questions
      .search({ ...baseParams, view, page, page_size: PAGE_SIZE, ...(grouped ? { group: 'duplicates' as const } : {}) })
      .then((response) => {
        if (cancelled) return;
        const data = response.data as AdminCatalogPage<QuestionLibrarySearchRow | QuestionLibraryDuplicateGroup>;
        if (grouped) {
          setGroups(data.results as QuestionLibraryDuplicateGroup[]);
          setRows([]);
        } else {
          setRows(data.results as QuestionLibrarySearchRow[]);
          setGroups([]);
        }
        setTotal(typeof data.count === 'number' ? data.count : data.results.length);
        setError(null);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load questions.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, grouped, page, refreshToken, view]);

  React.useEffect(() => {
    let cancelled = false;
    adminContent.questions
      .listFlows(filters.courseId !== 'all' ? { course_id: filters.courseId } : undefined)
      .then((response) => {
        if (!cancelled) setModules((response.data ?? []) as FlowOption[]);
      })
      .catch(() => {
        if (!cancelled) setModules([]);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.courseId]);

  // Identifies which group + filters a member request belongs to, so stale requests never go out.
  const groupScopeKey = `${openGroup ?? ''}|${JSON.stringify(baseParams)}|${view}|${refreshToken}`;

  React.useEffect(() => {
    setGroupRows([]);
    setGroupRowsFor(null);
    setGroupTotal(0);
    setGroupLoadedPage(0);
    setGroupError(false);
    setGroupRequest((current) => ({ key: groupScopeKey, page: 1, attempt: current.attempt + 1 }));
  }, [groupScopeKey]);

  React.useEffect(() => {
    if (openGroup === null) {
      return undefined;
    }
    if (groupRequest.key !== groupScopeKey) {
      return undefined;
    }
    let cancelled = false;
    const groupId = openGroup;
    const requestedPage = groupRequest.page;
    setGroupLoading(true);
    setGroupError(false);
    adminContent.questions
      .search({ ...baseParams, view, duplicate_of: groupId, page: requestedPage, page_size: GROUP_PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        const data = response.data as AdminCatalogPage<QuestionLibrarySearchRow>;
        setGroupRows((current) => (requestedPage === 1 ? data.results : [...current, ...data.results]));
        setGroupRowsFor(groupId);
        setGroupTotal(typeof data.count === 'number' ? data.count : data.results.length);
        setGroupLoadedPage(requestedPage);
      })
      .catch(() => {
        if (cancelled) return;
        // Keep what is already shown; the same page is requested again on retry.
        setGroupRowsFor(groupId);
        setGroupError(true);
      })
      .finally(() => {
        if (!cancelled) setGroupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [baseParams, groupRequest, groupScopeKey, openGroup, view]);

  const requestNextGroupPage = () =>
    setGroupRequest((current) => ({ key: groupScopeKey, page: groupLoadedPage + 1, attempt: current.attempt + 1 }));

  const visibleGroupRows = groupRowsFor === openGroup ? groupRows : [];

  const hasFilters =
    !!searchInput.trim()
    || filters.questionType !== 'all'
    || filters.courseId !== 'all'
    || filters.flowId !== 'all'
    || !!filters.keyPrefix.trim();
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE);

  return (
    <div className="space-y-3" data-testid="ql-search-panel">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Saved views">
        {QUESTION_LIBRARY_VIEWS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setView(option.value)}
            aria-pressed={view === option.value}
            data-testid={`ql-view-${option.value}`}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              view === option.value
                ? 'border-foundation-base-darkbase-dark-500 bg-foundation-base-darkbase-dark-500 text-white'
                : 'border-foundation-calm-blue-200 bg-white/70 text-foundation-base-darkbase-dark-500 hover:bg-white',
            )}
          >
            {option.label}
            {counts ? <span className="ml-1.5 opacity-70">{counts[option.value].toLocaleString()}</span> : null}
          </button>
        ))}
      </div>

      <div className="grid gap-3 rounded-3xl bg-white/40 p-3 md:grid-cols-2 xl:grid-cols-[minmax(14rem,2fr)_repeat(3,minmax(9rem,1fr))_minmax(8rem,0.8fr)]">
        <div className="space-y-1.5">
          <Label htmlFor="ql-search-input" className="text-xs uppercase text-muted-foreground">Search</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              id="ql-search-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Text, key, or ID"
              className="pl-9"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ql-search-type" className="text-xs uppercase text-muted-foreground">Question type</Label>
          <Select value={filters.questionType} onValueChange={(value) => setFilters((current) => ({ ...current, questionType: value }))}>
            <SelectTrigger id="ql-search-type" className="w-full"><SelectValue placeholder="Question type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {QUESTION_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ql-search-course" className="text-xs uppercase text-muted-foreground">Course</Label>
          <CoursePicker
            value={filters.courseId}
            onValueChange={(value) => setFilters((current) => ({ ...current, courseId: value, flowId: 'all' }))}
            options={courses.map((course) => ({ value: String(course.id), label: course.name }))}
            placeholder="Course"
            triggerId="ql-search-course"
            sentinelOption={{ value: 'all', label: 'All courses' }}
            emptyMessage="No courses found"
            maxVisibleOptions={50}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ql-search-module" className="text-xs uppercase text-muted-foreground">Module</Label>
          <FlowPicker
            value={filters.flowId}
            onValueChange={(value) => setFilters((current) => ({ ...current, flowId: value }))}
            options={modules.map((module) => ({ value: String(module.id), label: module.name }))}
            placeholder="Module"
            triggerId="ql-search-module"
            sentinelOption={{ value: 'all', label: filters.courseId !== 'all' ? 'All modules in this course' : 'All modules' }}
            emptyMessage="No modules found"
            maxVisibleOptions={50}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ql-search-key-prefix" className="text-xs uppercase text-muted-foreground">Key starts with</Label>
          <Input
            id="ql-search-key-prefix"
            value={filters.keyPrefix}
            onChange={(event) => setFilters((current) => ({ ...current, keyPrefix: event.target.value }))}
            placeholder="e.g. lp_"
            className="font-mono"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <span data-testid="ql-search-total">
            {grouped ? `${total.toLocaleString()} distinct texts` : `${total.toLocaleString()} questions`}
            {hasFilters ? ' match' : ''}
          </span>
          {hasFilters ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 font-medium text-foundation-orange-500"
              onClick={() => {
                setSearchInput('');
                setFilters(EMPTY_FILTERS);
              }}
            >
              <X className="h-3 w-3" aria-hidden="true" /> Clear filters
            </button>
          ) : null}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
          <Switch checked={grouped} onCheckedChange={setGrouped} aria-label="Group identical text" data-testid="ql-group-toggle" />
          Group identical text
        </label>
      </div>

      <div className="overflow-hidden rounded-3xl bg-white/40">
        {error ? <p className="px-4 py-3 text-sm text-destructive">{error}</p> : null}
        <div className="max-h-[26rem] overflow-auto">
          <table className="w-full min-w-[54rem] table-fixed text-sm">
            <thead className="sticky top-0 z-10 bg-white/90 text-left text-xs uppercase text-muted-foreground backdrop-blur">
              <tr>
                <th className="px-4 py-2 font-medium">Question</th>
                <th className="w-40 px-4 py-2 font-medium">Type</th>
                <th className="w-44 px-4 py-2 font-medium">Course</th>
                <th className="w-32 px-4 py-2 font-medium">Used in</th>
                <th className="w-24 px-4 py-2 font-medium">Edited</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && groups.length === 0 ? (
                <tr><td colSpan={5}><div className="flex justify-center py-10"><LoadingAnimation /></div></td></tr>
              ) : null}
              {!loading && rows.length === 0 && groups.length === 0 && !error ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No questions match.</td></tr>
              ) : null}
              {rows.map((row) => (
                <SearchResultRow key={row.id} row={row} selected={row.id === selectedQuestionId} onSelect={onSelectQuestion} />
              ))}
              {groups.map((group) => {
                const isOpen = openGroup === group.sample_question_id;
                const label = plainQuestionLabel(group.label);
                if (group.count === 1) {
                  return (
                    <tr
                      key={group.text_key || group.sample_question_id}
                      className={cn('cursor-pointer border-t border-black/5 hover:bg-white/50', group.sample_question_id === selectedQuestionId && 'bg-foundation-orange-50')}
                      onClick={() => onSelectQuestion(group.sample_question_id)}
                    >
                      <td className="max-w-0 px-4 py-2">
                        <button
                          type="button"
                          className="block w-full truncate rounded-md text-left font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectQuestion(group.sample_question_id);
                          }}
                        >
                          {label || <span className="italic text-destructive">(empty text)</span>}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">{formatQuestionTypeSlug(group.types[0])}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{group.course_count ? `${group.course_count} course${group.course_count > 1 ? 's' : ''}` : ''}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{group.module_count} module{group.module_count === 1 ? '' : 's'}</td>
                      <td />
                    </tr>
                  );
                }
                return (
                  <React.Fragment key={group.text_key || group.sample_question_id}>
                    <tr
                      className="cursor-pointer border-t border-black/5 hover:bg-white/50"
                      onClick={() => setOpenGroup(isOpen ? null : group.sample_question_id)}
                      data-testid={`ql-dupgroup-${group.sample_question_id}`}
                    >
                      <td className="max-w-0 px-4 py-2">
                        <button
                          type="button"
                          className="block w-full min-w-0 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-expanded={isOpen}
                          onClick={(event) => {
                            event.stopPropagation();
                            setOpenGroup(isOpen ? null : group.sample_question_id);
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">×{group.count}</span>
                            <span className="truncate font-medium text-foreground">{label}</span>
                          </span>
                          <span className="block text-[11px] text-muted-foreground">{isOpen ? 'Click to collapse' : 'Click to list them'}</span>
                        </button>
                      </td>
                      <td className="max-w-0 truncate px-4 py-2 text-xs text-muted-foreground">{group.types.map(formatQuestionTypeSlug).join(', ')}</td>
                      <td className="px-4 py-2 text-xs">{group.course_count} course{group.course_count === 1 ? '' : 's'}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{group.module_count} module{group.module_count === 1 ? '' : 's'}</td>
                      <td />
                    </tr>
                    {isOpen
                      ? visibleGroupRows.map((row) => (
                          <SearchResultRow key={row.id} row={row} nested selected={row.id === selectedQuestionId} onSelect={onSelectQuestion} />
                        ))
                      : null}
                    {isOpen && groupLoading ? (
                      <tr><td colSpan={5} className="px-4 py-2 pl-10 text-xs text-muted-foreground">Loading…</td></tr>
                    ) : null}
                    {isOpen && !groupLoading && groupError ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-2 pl-10">
                          <span role="alert" className="mr-3 text-xs text-destructive">Couldn’t load these questions.</span>
                          <Button type="button" variant="secondary" size="auto" className="px-3 py-1 text-xs" onClick={requestNextGroupPage}>
                            Retry
                          </Button>
                        </td>
                      </tr>
                    ) : null}
                    {isOpen && !groupLoading && !groupError && visibleGroupRows.length < groupTotal ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-2 pl-10">
                          <Button type="button" variant="secondary" size="auto" className="px-3 py-1 text-xs" onClick={requestNextGroupPage}>
                            Show {Math.min(GROUP_PAGE_SIZE, groupTotal - visibleGroupRows.length)} more of {groupTotal}
                          </Button>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/5 px-4 py-2 text-xs text-muted-foreground">
          <span>Showing {start}-{end} of {total.toLocaleString()}</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="auto" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1}>Previous</Button>
            <span>Page {page} of {totalPages}</span>
            <Button variant="ghost" size="auto" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionSearchPanel;
